/**
 * avaliacoesAPI.ts
 * Service para comunicação com os endpoints de Avaliação de Testes de Campo
 */
import api from '../../../services/apiConfig';

// ===========================================================================
// TIPOS / INTERFACES
// ===========================================================================

export type StatusAvaliacao = 'pendente' | 'em_teste' | 'preenchida' | 'expirada';
export type ResultadoTeste  = 'aprovado' | 'reprovado';
export type NivelAvaliacao  = 'ruim' | 'regular' | 'bom' | 'otimo';

/** Registro principal da ficha (amostra_avaliacoes) */
export interface Avaliacao {
    id: number;
    pedido_id: number;
    id_externo: number | null;
    tecnico_seguranca:    string | null;
    representante:         string | null;
    usuario_teste:         string | null;
    funcao_usuario:        string | null;
    setor_usuario:         string | null;
    parametro_comparacao:  string | null;
    data_aprovacao:        string | null;
    data_prazo_retorno:    string | null;
    data_inicio_teste:     string | null;
    data_fim_teste:        string | null;
    status_avaliacao:      StatusAvaliacao;
    resultado_teste:       ResultadoTeste | null;
    observacoes:           string | null;
    reclamacoes:           string | null;
    created_at:            string;
    updated_at:            string;
}

/** Métricas por referência (amostra_avaliacao_itens) */
export interface AvaliacaoItem {
    id: number;
    avaliacao_id: number;
    pedido_item_id: number | null;
    referencia: string;
    // Escala 1-5
    estetica:           number | null;
    conforto_peito:     number | null;
    conforto_dedos:     number | null;
    conforto_calcanhar: number | null;
    calcar:             number | null;
    descalcar:          number | null;
    aderencia:          number | null;
    temperatura:        number | null;
    peso:               number | null;
    limpeza:            number | null;
    // Critérios gerais
    avaliacao_conforto:          NivelAvaliacao | null;
    avaliacao_durabilidade:      NivelAvaliacao | null;
    avaliacao_protecao:          NivelAvaliacao | null;
    avaliacao_custo_beneficio:   NivelAvaliacao | null;
    
    // Resultado por referência
    resultado_teste:             ResultadoTeste | null;
}

/** Payload para criar/atualizar avaliação */
export interface UpsertAvaliacaoPayload {
    tecnico_seguranca?:    string;
    representante?:         string;
    usuario_teste?:         string;
    funcao_usuario?:        string;
    setor_usuario?:         string;
    parametro_comparacao?:  string;
    data_inicio_teste?:     string;
    data_fim_teste?:        string;
    resultado_teste?:       ResultadoTeste;
    observacoes?:           string;
    reclamacoes?:           string;
    status_avaliacao?:      StatusAvaliacao;
    itens:                  Omit<AvaliacaoItem, 'id' | 'avaliacao_id'>[];
}

/** Resposta do GET /api/avaliacoes/amostras/:pedidoId */
export interface AvaliacaoResponse {
    avaliacao: Avaliacao | null;
    itens: AvaliacaoItem[];
    is_aws_env?: boolean;
}

/** Item da lista de fichas pendentes */
export interface FichaPendente {
    id: number;
    pedido_id: number;
    id_externo: number | null;
    status_avaliacao: StatusAvaliacao;
    data_aprovacao: string | null;
    data_prazo_retorno: string | null;
    data_inicio_teste: string | null;
    data_fim_teste: string | null;
    resultado_teste: ResultadoTeste | null;
    tecnico_seguranca: string | null;
    representante: string | null;
    reclamacoes: string | null;
    cliente_id: number;
    cliente_nome: string;
    dias_restantes: number;
}

export interface FichasPendentesResponse {
    items: FichaPendente[];
    pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
    };
}

/** Dados do dashboard de avaliações */
export interface AvaliacoesDashboardData {
    kpis: {
        totalAvaliacoes: number;
        totalAprovados:  number;
        totalReprovados: number;
        taxaAprovacao:   number;
        totalQuantidadeAmostras: number;
        totalValorAmostras:      number;
    };
    criterios: {
        mediaConforto:       number;
        mediaDurabilidade:   number;
        mediaProtecao:       number;
        mediaCustoBeneficio: number;
    };
    topReps: {
        vendedor:      string;
        quantidade:    number;
        valor:         number;
        notaMedia:     number;
        taxaAprovacao: number;
    }[];
    topSegmentos: {
        segmento:      string;
        quantidade:    number;
        taxaAprovacao: number;
    }[];
    topReferencias: {
        referencia: string;
        quantidade: number;
        valor:      number;
        notaMedia:  number;
    }[];
    topClientes: {
        cliente:    string;
        quantidade: number;
        valor:      number;
    }[];
}

// ===========================================================================
// FUNÇÕES DA API
// ===========================================================================

/**
 * Lista fichas de avaliação pendentes/expiradas (painel de gestão)
 */
export const fetchFichasPendentes = (page = 1, limit = 20) =>
    api.get<FichasPendentesResponse>('/avaliacoes/amostras/pendentes', {
        params: { page, limit },
    });

/**
 * Busca a avaliação de um pedido específico (se existir)
 */
export const fetchAvaliacaoPorPedido = (pedidoId: number) =>
    api.get<AvaliacaoResponse>(`/avaliacoes/amostras/${pedidoId}`);

/**
 * Cria ou atualiza a avaliação de um pedido (upsert)
 */
export const salvarAvaliacao = (pedidoId: number, payload: UpsertAvaliacaoPayload) =>
    api.post<{ success: boolean; avaliacaoId: number; message: string }>(
        `/avaliacoes/amostras/${pedidoId}`,
        payload
    );

/**
 * Dados agregados para o Dashboard de BI de Amostras
 */
export const fetchAvaliacoesDashboard = (dataI: string, dataF: string) =>
    api.get<AvaliacoesDashboardData>('/avaliacoes/amostras/dashboard', {
        params: { dataI, dataF },
    });
